import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useIsMobile } from "@/components/shared/PagePrimitives";
import { ListHeader, ListToolbar } from "@/components/shared/ListToolbar";
import { useListFilters } from "@/hooks/useListFilters";
import { useGroupSort, GroupedGrid } from "@/components/shared/GroupSort";
import { InvestorEditDialog } from "@/components/InvestorEditDialog";
import { useAdresarView, AdresarSeznam, KontaktBunka } from "@/components/shared/Adresar";
import { Building2, Mail, Phone, List, LayoutGrid, Briefcase, MapPin, Hash, Clock, CaseSensitive } from "lucide-react";
import { plur } from "@/lib/format";

const norm = (s) => (s || "").normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase();

// 317: Seznam (výchozí) / Karty + hledání — stejně jako Dodavatelé.
// 353: jednotná lišta — Filtr Zakázky (běží / bez zakázky), Nový investor (N).
// 378: Seskupit a řadit (jako Doklady) — skupiny Typ / Město / Zakázky, řazení Jméno / Počet zakázek / Datum přidání.
const GROUPS = {
  type: { label: "Typ", icon: Building2, key: (i) => (i.type === "person" ? "person" : "company"), name: (k) => (k === "person" ? "Soukromé osoby" : "Firmy") },
  city: { label: "Město", icon: MapPin, key: (i) => (i.city || "").trim(), none: "Bez města" },
  projects: { label: "Zakázky", icon: Briefcase, key: (i) => (i.projects_active > 0 ? "a" : i.projects_total ? "b" : ""), order: (a, b) => a.localeCompare(b),
    name: (k) => ({ a: "S běžící zakázkou", b: "Jen hotové zakázky" }[k]), none: "Bez zakázky", dirs: ["Běžící první", "Bez zakázky první"] },
};
const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "cs");
const ORDERS = {
  name: { label: "Jméno", icon: CaseSensitive, dirs: ["A → Z", "Z → A"], def: "asc", val: (i) => i.name || "" },
  projects: { label: "Počet zakázek", icon: Hash, dirs: ["Od nejmenšího", "Od největšího"], def: "desc", val: (i) => i.projects_total || 0 },
  created: { label: "Datum přidání", icon: Clock, dirs: ["Nejstarší", "Nejnovější"], def: "desc", val: (i) => i.created_at || "" },
};
const VIEWS = [{ value: "list", label: "Seznam", icon: List, testid: "investors-view-list" }, { value: "cards", label: "Karty", icon: LayoutGrid, testid: "investors-view-cards" }];

export default function Investors() {
  const mobile = useIsMobile();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useAdresarView("investors_view");
  const q = norm(useDeferredValue(search).trim());
  const groups = useMemo(() => [{ key: "zakazky", label: "Zakázky", icon: Briefcase,
    options: [{ value: "bezi", label: "S běžící zakázkou" }, { value: "zadna", label: "Bez zakázky" }],
    match: (i, v) => (v === "bezi" ? i.projects_active > 0 : !i.projects_total) }], []);
  const F = useListFilters(items || [], { groups, entity: { one: "investor", few: "investoři", many: "investorů" } });

  const load = () => api.get("/investors").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const found = useMemo(() => F.visible.filter((i) => !q || norm([i.name, i.ico, i.city, i.contact_person, i.email, i.phone].join(" ")).includes(q)), [F.visible, q]);
  const GS = useGroupSort(found, { storageKey: "investors_order", legacyKey: "investors_sort", groups: GROUPS, orders: ORDERS, def: { g: "none", gd: 0, k: "name", dir: "asc" }, tie: byName });
  const shown = GS.sorted;
  const open = (i) => nav(`/investori/${i.id}`);

  return (
    <div className="space-y-5" data-testid="investors-page">
      <ListHeader title="Investoři" mobile={mobile} testid="investors-header"
        summary={items ? `${plur(items.length, "investor", "investoři", "investorů")} · pro koho stavíte` : null}
        search={search} onSearch={setSearch} searchPh="Hledat jméno, IČO, město…"
        primary={{ label: "Investor", full: "Nový investor", onClick: () => setCreateOpen(true), testid: "new-investor-btn" }} />

      {(items?.length || 0) > 0 && (
        <ListToolbar mobile={mobile} testid="investors-toolbar" filter={F} search={search} onSearch={setSearch} searchPh="Hledat jméno, IČO, město…"
          sort={GS.sortProp}
          views={VIEWS} view={view} onView={setView} />
      )}

      {shown.length > 0 && view === "list" && (
        <AdresarSeznam rows={shown} onOpen={open} testid="investors-list" rowTestid={(i) => `investor-${i.id}`} groups={GS.groups} closed={GS.closed} onToggleGroup={GS.toggle}
          cols={[
            { label: "Investor", width: "minmax(0,2fr)", cell: (i) => (<>
              <span className="block font-bold text-[15px] truncate">{i.name}</span>
              {i.contact_person && <span className="block text-muted-foreground truncate">{i.contact_person}</span>}
            </>) },
            { label: "IČO", width: "110px", cell: (i) => <span className="tabular-nums">{i.ico || <span className="text-muted-foreground">—</span>}</span> },
            { label: "Sídlo", width: "minmax(0,1fr)", cell: (i) => <span className="block truncate">{i.city || <span className="text-muted-foreground">—</span>}</span> },
            { label: "Kontakt", width: "minmax(0,1.4fr)", cell: (i) => <KontaktBunka email={i.email} phone={i.phone} /> },
            { label: "Zakázky", width: "110px", right: true, cell: (i) => i.projects_total ? (<>
              <span className="block font-bold tabular-nums">{i.projects_total}</span>
              {i.projects_active > 0 && <span className="block text-xs text-muted-foreground tabular-nums">{i.projects_active} aktivní</span>}
            </>) : <span className="text-muted-foreground">—</span> },
          ]}
          mobile={(i) => ({
            title: i.name,
            sub: [i.ico && `IČO ${i.ico}`, i.city, i.contact_person].filter(Boolean).join(" · ") || i.email || i.phone,
            side: i.projects_total ? plur(i.projects_total, "zakázka", "zakázky", "zakázek") : null,
          })} />
      )}

      {shown.length > 0 && view === "cards" && (
        <GroupedGrid groups={GS.groups} rows={shown} closed={GS.closed} onToggle={GS.toggle} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(i) => (
            <button key={i.id} onClick={() => open(i)} data-testid={`investor-${i.id}`}
              className="text-left bg-card border border-border rounded-[20px] p-5 hover:border-brand hover:-translate-y-0.5 transition-all motion-reduce:transition-none motion-reduce:hover:translate-y-0">
              <div className="flex items-center gap-2 mb-2 min-w-0"><Building2 size={16} className="text-brand shrink-0" /><span className="font-bold truncate">{i.name}</span></div>
              {i.contact_person && <div className="text-sm text-muted-foreground truncate">{i.contact_person}</div>}
              {(i.ico || i.city) && <div className="text-xs text-muted-foreground truncate">{[i.ico && `IČO ${i.ico}`, i.city].filter(Boolean).join(" · ")}</div>}
              <div className="mt-3 space-y-1 text-sm">
                {i.email && <div className="flex items-center gap-2 text-muted-foreground min-w-0"><Mail size={13} className="shrink-0" /><span className="truncate">{i.email}</span></div>}
                {i.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone size={13} className="shrink-0" /> {i.phone}</div>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 text-[12px] font-semibold">
                <span className="px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{i.projects_total ? plur(i.projects_total, "zakázka", "zakázky", "zakázek") : "zatím bez zakázky"}</span>
                {i.projects_active > 0 && <span className="px-2 py-0.5 rounded-full bg-brand/10 pp-ink">{i.projects_active} aktivní</span>}
              </div>
            </button>
          )}
        </GroupedGrid>
      )}

      {items && items.length === 0 && <p className="text-muted-foreground" data-testid="investors-empty">Zatím žádní investoři. Přidejte firmu podle IČO, nebo fyzickou osobu.</p>}
      {items && items.length > 0 && !shown.length && <p className="text-muted-foreground">Nic nenalezeno.</p>}

      <InvestorEditDialog open={createOpen} onOpenChange={setCreateOpen} mode="create" onSaved={load} />
    </div>
  );
}
